import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Beds, CarName, Guest, HotelSample, Location, Sqaure } from '../../../assests/ExportImageIcon'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import { buildFlightTripPaymentFareLines, formatInr, heroStatusMeta, isTicketInProgress, tripHeroTitle } from '../../../utils/flightFare'
import { splitBookingLegs } from '../../../utils/flightItinerary'
import './tripFlightDetail.css'
import { formatMoney, daysBetween } from '../../../utils/formatMoney'
import { getClientIp } from '../../../utils/clientIp'
import { PAYMENT_METHOD_LABELS } from '../../../utils/venueFormat'
import { isEventService, normalizeServiceCode } from '../../../utils/serviceCode'
import { showToast } from '../../../componants/toast/toastService'
import PackageCancelButton from '../../../componants/packageCancel/PackageCancelButton'
import CarSearchLoader from '../../../componants/carSearchLoader/CarSearchLoader'

const FLIGHT_IMPORTANT_INFO = [
    'Carry a valid government ID matching the passenger name on this ticket.',
    'Download or save your boarding pass before heading to the airport.',
    'Check baggage allowance for your fare class before packing.',
    'Arrive at least 2 hours early for domestic and 3 hours for international flights.',
]

const ptcLabel = (ptc) => ({ ADT: 'Adult', CHD: 'Child', INF: 'Infant' }[String(ptc || '').toUpperCase()] || ptc || 'Adult')

const genderLabel = (g) => {
    const value = String(g || '').toUpperCase()
    if (value === 'F' || value === 'FEMALE') return 'Female'
    if (value === 'M' || value === 'MALE') return 'Male'
    return g || ''
}

const CAR_IMPORTANT_INFO = [
    'Carry a valid government-issued photo ID and driving license',
    'Fuel/mileage policy applies as per the rental conditions',
    'Security deposit is held/charged at pick-up',
    'Reach the pick-up location on time to avoid a no-show',
]

const HOTEL_IMPORTANT_INFO = [
    'Carry a valid government-issued photo ID for check-in',
    'Confirm hotel check-in / check-out times before travel',
    'Keep your booking confirmation handy at the front desk',
]

const PACKAGE_IMPORTANT_INFO = [
    'Carry a valid government-issued photo ID matching the traveler details',
    'Booking confirmation and vouchers will be sent via email, SMS and WhatsApp',
    'Contact support if you need to modify or cancel this booking',
]

// "YYYY-MM-DD HH:MM:SS" -> { date: 'YYYY-MM-DD', time: 'HH:MM:SS' }
const splitDateTime = (value) => {
    if (!value) return { date: '-', time: '' }
    const [date, time] = value.split(' ')
    return { date: date || '-', time: time || '' }
}

const DateTimeStack = ({ value }) => {
    const { date, time } = splitDateTime(value)
    return (
        <span className='tripDocDateTime'>
            <span>{date}</span>
            {time && <span className='tripDocDateTime__time'>{time}</span>}
        </span>
    )
}

const GoldStar = ({ filled = true }) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M7 10.65 2.89 12.95 3.8 8.33.34 5.13l4.68-.55L7 .29l1.98 4.29 4.68.55-3.46 3.2.92 4.62Z" fill={filled ? '#F5A623' : '#E5E7EB'} />
    </svg>
)

// Clickable 5-star input used only in the "edit your review" form below.
const EditableStars = ({ value, onChange }) => {
    const [hovered, setHovered] = useState(0)
    return (
        <div className='tripDocRating__stars' onMouseLeave={() => setHovered(0)}>
            {[1, 2, 3, 4, 5].map((star) => (
                <button
                    type='button'
                    key={star}
                    className='tripDocRating__starBtn'
                    onMouseEnter={() => setHovered(star)}
                    onClick={() => onChange(star)}
                    aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                >
                    <GoldStar filled={star <= (hovered || value)} />
                </button>
            ))}
        </div>
    )
}

// Field names match the real GET /bookings/ratings/view/{rating_id} response.
const RATING_CATEGORY_ROWS = [
    { key: 'cleanliness', label: 'Cleanliness' },
    { key: 'service_rate', label: 'Service' },
    { key: 'location', label: 'Location' },
    { key: 'value_for_money', label: 'Value for Money' },
]

// Shows the review the user already submitted for this booking, if any —
// silently renders nothing when there isn't one yet (a 404 is expected for
// any booking that hasn't been rated) — plus a "Reply" action so the vendor
// can respond to it via POST /bookings/rating/{rating_id}/reply/.
const TripRatingSection = ({ bookingId }) => {
    const [rating, setRating] = useState(null)
    const [loaded, setLoaded] = useState(false)
    const [replying, setReplying] = useState(false)
    const [replyText, setReplyText] = useState('')
    const [submittingReply, setSubmittingReply] = useState(false)
    const [sentReply, setSentReply] = useState('')
    const [editing, setEditing] = useState(false)
    const [editForm, setEditForm] = useState(null)
    const [savingEdit, setSavingEdit] = useState(false)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        if (!bookingId) {
            setLoaded(true)
            return
        }
        let cancelled = false
        setLoaded(false)
        service
            .get(endpoints.bookings.ratingView.replace('{rating_id}', bookingId), {}, { silent: true })
            .then(({ data }) => { if (!cancelled) setRating(data.data || data) })
            .catch(() => { if (!cancelled) setRating(null) })
            .finally(() => { if (!cancelled) setLoaded(true) })
        return () => { cancelled = true }
    }, [bookingId])

    if (!loaded || !rating) return null

    const overall = Math.max(0, Math.min(5, Math.round(Number(rating.rating) || 0)))

    const handleSubmitReply = () => {
        if (submittingReply || !replyText.trim()) return
        setSubmittingReply(true)
        service
            .post(endpoints.bookings.ratingReply.replace('{rating_id}', bookingId), { reply: replyText.trim() })
            .then(() => {
                setSentReply(replyText.trim())
                setReplying(false)
            })
            .finally(() => setSubmittingReply(false))
    }

    const handleStartEdit = () => {
        setEditForm({
            title: rating.title || '',
            description: rating.description || '',
            rating: Number(rating.rating) || 0,
            cleanliness: Number(rating.cleanliness) || 0,
            service_rate: Number(rating.service_rate) || 0,
            location: Number(rating.location) || 0,
            value_for_money: Number(rating.value_for_money) || 0,
        })
        setEditing(true)
    }

    const handleSaveEdit = () => {
        if (savingEdit) return
        setSavingEdit(true)
        service
            .put(endpoints.bookings.ratingUpdate.replace('{rating_id}', bookingId), editForm)
            .then(({ data }) => {
                setRating(data.data || { ...rating, ...editForm })
                setEditing(false)
                showToast('Review updated', 'success')
            })
            .finally(() => setSavingEdit(false))
    }

    const handleDelete = () => {
        if (deleting || !window.confirm('Delete your review? This cannot be undone.')) return
        setDeleting(true)
        service
            .delete(endpoints.bookings.ratingDelete.replace('{rating_id}', bookingId))
            .then(() => {
                setRating(null)
                showToast('Review deleted', 'success')
            })
            .finally(() => setDeleting(false))
    }

    return (
        <div className='bsSection'>
            <div className='bsSectionTitle'>Your Rating</div>
            <div className='bsBox'>
                {editing ? (
                    <div className='tripDocRating__editForm'>
                        <label className='tripDocRating__editLabel'>Overall Rating</label>
                        <EditableStars value={editForm.rating} onChange={(value) => setEditForm((prev) => ({ ...prev, rating: value }))} />

                        <label className='tripDocRating__editLabel'>Title</label>
                        <input
                            type='text'
                            value={editForm.title}
                            onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))}
                        />

                        <label className='tripDocRating__editLabel'>Review</label>
                        <textarea
                            rows={3}
                            value={editForm.description}
                            onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
                        />

                        <div className='tripDocRating__categoryEditGrid'>
                            {RATING_CATEGORY_ROWS.map((category) => (
                                <div key={category.key}>
                                    <label className='tripDocRating__editLabel'>{category.label}</label>
                                    <EditableStars
                                        value={editForm[category.key]}
                                        onChange={(value) => setEditForm((prev) => ({ ...prev, [category.key]: value }))}
                                    />
                                </div>
                            ))}
                        </div>

                        <div className='tripDocRating__replyActions'>
                            <button type='button' className='bookingDownloadBtn' onClick={() => setEditing(false)} disabled={savingEdit}>
                                Cancel
                            </button>
                            <button type='button' className='commonBtn' onClick={handleSaveEdit} disabled={savingEdit}>
                                {savingEdit ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className='tripDocRating__stars'>
                            {Array.from({ length: 5 }).map((_, index) => (
                                <GoldStar key={index} filled={index < overall} />
                            ))}
                        </div>
                        {rating.title && <div className='hotelCard__name mt-2'>{rating.title}</div>}
                        {rating.description && <p className='tripDocRating__text'>{rating.description}</p>}
                        <div className='bsFareDivider' />
                        {RATING_CATEGORY_ROWS.filter((category) => Number(rating[category.key]) > 0).map((category) => (
                            <div className='bsFareRow' key={category.key}>
                                <span>{category.label}</span>
                                <span>{rating[category.key]} / 5</span>
                            </div>
                        ))}

                        <div className='bsFareDivider' />
                        <div className='tripDocRating__actions'>
                            <button type='button' className='bookingDownloadBtn' onClick={handleStartEdit}>
                                Edit
                            </button>
                            <button type='button' className='bookingDownloadBtn tripDocCancelBtn' onClick={handleDelete} disabled={deleting}>
                                {deleting ? 'Deleting...' : 'Delete'}
                            </button>
                            {!sentReply && !replying && (
                                <button type='button' className='bookingDownloadBtn' onClick={() => setReplying(true)}>
                                    Reply
                                </button>
                            )}
                        </div>

                        {sentReply && (
                            <>
                                <div className='bsFareDivider' />
                                <div className='tripDocRating__replyLabel'>Your Reply</div>
                                <p className='tripDocRating__text'>{sentReply}</p>
                            </>
                        )}

                        {replying && (
                            <div className='tripDocRating__replyBox'>
                                <textarea
                                    rows={3}
                                    placeholder='Write a reply to this review...'
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                />
                                <div className='tripDocRating__replyActions'>
                                    <button type='button' className='bookingDownloadBtn' onClick={() => setReplying(false)} disabled={submittingReply}>
                                        Cancel
                                    </button>
                                    <button type='button' className='commonBtn' onClick={handleSubmitReply} disabled={submittingReply || !replyText.trim()}>
                                        {submittingReply ? 'Sending...' : 'Submit Reply'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

const formatBookingDate = (value) => {
    if (!value) return '—'
    const raw = String(value).trim()
    const dateOnly = raw.match(/^(\d{4}-\d{2}-\d{2})/)
    const date = dateOnly ? new Date(`${dateOnly[1]}T00:00:00`) : new Date(raw)
    if (Number.isNaN(date.getTime())) return raw
    const day = date.toLocaleDateString('en-GB', { day: '2-digit' })
    const month = date.toLocaleDateString('en-GB', { month: 'short' })
    const year = date.getFullYear()
    const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' })
    return `${day} ${month} ${year}, ${weekday}`
}

const formatBookingDateTime = (value) => {
    if (!value) return '—'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
    })
}

// The venue trip-detail API literally sends the string "N/A" for unset fields
// instead of null/empty — normalize it to the same "—" the rest of the app uses.
const naToDash = (value) => (!value || String(value).trim().toUpperCase() === 'N/A' ? '—' : value)

const guestName = (guest) => [guest.title, guest.first_name, guest.last_name].filter(Boolean).join(' ') || 'Guest'

const isPlaceholderDate = (value) => {
    const text = String(value || '').trim()
    return !text || text.startsWith('0001-01-01')
}

const asNumber = (value) => {
    const n = Number(value)
    return Number.isFinite(n) ? n : 0
}

const DlRow = ({ label, value, children }) => {
    if (!children && (value == null || value === '')) return null
    return (
        <li>
            <span>{label}</span>
            <strong>{children || String(value)}</strong>
        </li>
    )
}

const ticketStatusRow = (trip) => {
    const stage = String(trip?.ticket_status?.stage || '').trim()
    if (stage) return stage
    return String(trip?.ticket_status_label || '').trim()
}

const buildFlightFareLines = (booking) =>
    buildFlightTripPaymentFareLines(booking, (amount) => formatMoney(amount, booking.currency || 'INR'))

const FlightSegmentCard = ({ seg, booking, index }) => {
    const airline = seg.airline_name || booking.airline_name || 'Flight'
    const flightNo = seg.flight_number || seg.flight_number_label || ''
    const iconUrl = seg.airline_icon_url || seg.icon_path || booking.airline_icon_url || ''
    const from = seg.departure_code || seg.from_code || booking.from_code || ''
    const to = seg.arrival_code || seg.to_code || booking.to_code || ''
    const fromPlace = seg.departure_label || seg.departure_city || seg.departure_airport_name || from
    const toPlace = seg.arrival_label || seg.arrival_city || seg.arrival_airport_name || to
    const pnr = seg.pnr || ''
    const bag = seg.baggage && typeof seg.baggage === 'object' ? seg.baggage : {}
    const bagLabel = bag.label
        || [bag.check_in && `Check-in ${bag.check_in}`, bag.cabin && `Cabin ${bag.cabin}`].filter(Boolean).join(' · ')
    const terminals = [
        seg.departure_terminal ? `Dep T${seg.departure_terminal}` : '',
        seg.arrival_terminal ? `Arr T${seg.arrival_terminal}` : '',
    ].filter(Boolean).join(' · ')

    return (
        <article className='confirm-card'>
            <div className='confirm-flight-head'>
                <div className='confirm-airline'>
                    {iconUrl ? <img src={iconUrl} alt="" /> : null}
                    <div>
                        <strong>{airline}</strong>
                        <span className='confirm-route-code'>{from} → {to}</span>
                    </div>
                </div>
                <span className='trips-flight-tag'>{flightNo || `Seg ${index + 1}`}</span>
            </div>
            <div className='confirm-times'>
                <div>
                    <div className='time'>{seg.departure_time || '—'} {from}</div>
                    <p className='place'>{fromPlace}</p>
                </div>
                <div className='confirm-mid'>
                    <span>{seg.duration || '—'}</span>
                    <i />
                    <span>{seg.stops_label || 'Non-stop'}</span>
                </div>
                <div className='confirm-times-arrive'>
                    <div className='time'>{seg.arrival_time || '—'} {to}</div>
                    <p className='place'>{toPlace}</p>
                </div>
            </div>
            <div className='confirm-meta'>
                {pnr ? <span>PNR: <strong>{pnr}</strong></span> : null}
                {terminals ? <span>{terminals}</span> : null}
                {bagLabel ? <span>Baggage: <strong>{bagLabel}</strong></span> : null}
                {seg.aircraft ? <span>{seg.aircraft}</span> : null}
                {seg.web_checkin_url ? (
                    <a href={seg.web_checkin_url} target='_blank' rel='noopener noreferrer'>Web check-in</a>
                ) : null}
            </div>
        </article>
    )
}

const FlightLegPanel = ({ heading, dateLabel, rows, booking, startIndex, extraInfo }) => {
    if (!rows.length) return null
    const first = rows[0] || {}
    const last = rows[rows.length - 1] || first
    const from = extraInfo?.from_code || first.departure_code || first.from_code || ''
    const to = extraInfo?.to_code || last.arrival_code || last.to_code || ''
    const route = from && to ? `${from} → ${to}` : ''
    const cityFrom = extraInfo?.departure_label || from
    const cityTo = extraInfo?.arrival_label || to
    const routeTitle = extraInfo?.route_label || [cityFrom, cityTo].filter(Boolean).join(' ↔ ') || heading
    const duration = extraInfo?.duration || first.duration || ''
    const stopsLabel = extraInfo?.stops_label
        || (rows.length > 1
            ? `${rows.length - 1} Stop${rows.length > 2 ? 's' : ''}`
            : (first.stops_label || 'Non-stop'))

    return (
        <section className='review-leg-panel'>
            <header className='review-leg-banner trips-leg-banner'>
                <div className='review-leg-banner-main'>
                    <strong className='review-leg-route-chip'>{heading}{route ? ` · ${route}` : ''}</strong>
                </div>
                {dateLabel ? <span className='review-leg-fare-chip'>{dateLabel}</span> : null}
            </header>
            <article className='fp-card fp-flight-card'>
                <section className='review-itinerary-leg'>
                    <div className='review-itinerary-leg-head'>
                        <h3 className='review-itinerary-title'>{routeTitle}</h3>
                        <ul className='review-itinerary-summary'>
                            {dateLabel ? <li>{dateLabel}</li> : null}
                            {duration ? <li>{duration}</li> : null}
                            <li>{stopsLabel}</li>
                        </ul>
                    </div>
                    <div className='review-itinerary-body'>
                        {rows.map((seg, index) => (
                            <FlightSegmentCard
                                key={`${seg.flight_number || index}-${startIndex + index}`}
                                seg={seg}
                                booking={booking}
                                index={startIndex + index}
                            />
                        ))}
                    </div>
                </section>
            </article>
        </section>
    )
}

const BackArrow = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M15 9H4M8 5l-4 4 4 4" stroke="#026D4D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const normalizeService = (value) => normalizeServiceCode(value)

const TripDoc = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams] = useSearchParams()

    const bookingId = searchParams.get('id') || location.state?.bookingId || null
    const serviceHint = normalizeService(searchParams.get('service') || location.state?.service)

    const [booking, setBooking] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [invoiceDownloading, setInvoiceDownloading] = useState(false)
    const [ticketDownloading, setTicketDownloading] = useState(false)
    const [cancelLoading, setCancelLoading] = useState(false)

    useEffect(() => {
        if (!bookingId) {
            setLoading(false)
            setError('No trip selected.')
            return
        }
        setLoading(true)
        setError('')
        service
            .get(
                endpoints.users.tripDetail.replace('{booking_id}', bookingId),
                {},
                { silent: true },
            )
            .then(({ data }) => setBooking(data.data))
            .catch((err) => setError(err.response?.data?.message || 'Could not load this trip.'))
            .finally(() => setLoading(false))
    }, [bookingId])

    const [eventCancelLoading, setEventCancelLoading] = useState(false)
    const [guestActionLoading, setGuestActionLoading] = useState(null)
    const [venuePayNextLoading, setVenuePayNextLoading] = useState(false)

    const serviceCode = normalizeService(booking?.service || serviceHint)
    const isCarTrip = serviceCode === 'car-rental'
    const isHotelTrip = serviceCode === 'hotel'
    const isEventTrip = isEventService(serviceCode)
    const isVenueTrip = serviceCode === 'venue'
    const isPackageTrip = serviceCode === 'travel_package'

    const handleDownloadInvoice = async () => {
        // booking.id is the real numeric id the ticket/invoice/detail paths use —
        // booking.booking_id can be a display code (e.g. venue's "BK-182"), not a
        // usable path segment. Prefer id, fall back to booking_id only if missing.
        const id = booking?.id || booking?.booking_id
        if (!id || invoiceDownloading) return
        setInvoiceDownloading(true)
        try {
            const url = endpoints.users.tripInvoice.replace('{booking_id}', id)
            const response = await service.download(url)
            const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
            const link = document.createElement('a')
            link.href = blobUrl
            link.download = `${serviceCode || 'trip'}-invoice-${booking.provider_booking_id || id}.pdf`
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(blobUrl)
        } catch {
            // error toast already shown by the service interceptor
        } finally {
            setInvoiceDownloading(false)
        }
    }

    const handleDownloadTicket = async () => {
        const id = booking?.id || booking?.booking_id
        if (!id || ticketDownloading) return
        setTicketDownloading(true)
        try {
            const url = endpoints.users.tripTicket.replace('{booking_id}', id)
            const response = await service.download(url)
            const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
            const link = document.createElement('a')
            link.href = blobUrl
            link.download = `${serviceCode || 'trip'}-ticket-${booking.provider_booking_id || id}.pdf`
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(blobUrl)
        } catch {
            // error toast already shown by the service interceptor
        } finally {
            setTicketDownloading(false)
        }
    }

    const handleCancelBooking = async () => {
        if (!booking?.provider_booking_id || (booking.status || '').toUpperCase() === 'CANCELLED' || cancelLoading) return
        setCancelLoading(true)
        try {
            const ip = await getClientIp()
            const { data } = await service.post(endpoints.cars.cancel, {
                ip_address: ip,
                confirmation_id: booking.provider_booking_id,
            }, { timeout: 45000 })
            setBooking((prev) => ({
                ...prev,
                status: data.data?.status || 'CANCELLED',
                status_label: data.data?.status || 'Cancelled',
                customer_name: data.data?.customer_name || prev.customer_name,
            }))
        } catch {
            // success/error toasts come from the service interceptor
        } finally {
            setCancelLoading(false)
        }
    }

    const handleCancelEventBooking = async () => {
        const id = booking?.booking_id
        if (!id || String(booking.status).toLowerCase() !== 'confirmed' || eventCancelLoading) return
        setEventCancelLoading(true)
        try {
            const { data } = await service.post(endpoints.customerEvents.cancel.replace('{booking_id}', id), {})
            setBooking((prev) => ({ ...prev, status: data.data?.status || 'cancelled' }))
        } catch {
            // success/error toasts come from the service interceptor
        } finally {
            setEventCancelLoading(false)
        }
    }

    const handleGuestCheckin = async (guestId) => {
        if (!guestId || guestActionLoading) return
        setGuestActionLoading(guestId)
        try {
            const { data } = await service.post(endpoints.customerEvents.guestCheckin.replace('{guest_id}', guestId), {})
            setBooking((prev) => ({
                ...prev,
                guests: (prev.guests || []).map((g) => (g.id === guestId ? { ...g, checkin: data.data?.checkin || new Date().toISOString() } : g)),
            }))
            showToast('Guest checked in', 'success')
        } catch {
            // error toast already shown by the service interceptor
        } finally {
            setGuestActionLoading(null)
        }
    }

    const handleGuestCheckout = async (guestId) => {
        if (!guestId || guestActionLoading) return
        setGuestActionLoading(guestId)
        try {
            const { data } = await service.post(endpoints.customerEvents.guestCheckout.replace('{guest_id}', guestId), {})
            setBooking((prev) => ({
                ...prev,
                guests: (prev.guests || []).map((g) => (g.id === guestId ? { ...g, checkout: data.data?.checkout || new Date().toISOString() } : g)),
            }))
            showToast('Guest checked out', 'success')
        } catch {
            // error toast already shown by the service interceptor
        } finally {
            setGuestActionLoading(null)
        }
    }

    if (!bookingId || error) {
        return (
            <section className='commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <div className='tripDocHeader' onClick={() => navigate(-1)}>
                        <BackArrow />
                        <span className='bsSectionTitle'>Trip Detail</span>
                    </div>
                    <p className='fare-extra-hint mt-3'>{error || 'No trip selected.'}</p>
                </div>
            </section>
        )
    }

    if (loading || !booking) {
        return (
            <section className='commonTopPadding commonBottomPadding'>
                <div className='container'>
                    {isCarTrip ? (
                        <CarSearchLoader text='Loading booking details...' />
                    ) : (
                        <>
                            <div className='tripDocHeader' onClick={() => navigate(-1)}>
                                <BackArrow />
                                <span className='bsSectionTitle'>Trip Detail</span>
                            </div>
                            <p className='fare-extra-hint mt-3'>Loading trip...</p>
                        </>
                    )}
                </div>
            </section>
        )
    }

    // ---- Car branch ----
    if (isCarTrip) {
        const carDetails = booking.car_details || booking.car || {}
        const fee = booking.payment_summary || {}
        const pax = booking.passenger_details || {}
        const contact = booking.contact_details || {}
        const status = (booking.status_label || booking.status || '').toUpperCase()
        const carCancelled = String(booking.status || '').toLowerCase() === 'cancelled'
        const pickupDate = booking.pickup?.date_time
            ? new Date(booking.pickup.date_time.replace(' ', 'T'))
            : null
        const canCancel = !carCancelled && (!pickupDate || pickupDate > new Date())

        const CAR_BOOKING = {
            bookingId: booking.provider_booking_id || booking.booking_id || booking.id || '—',
            title: booking.car_name || carDetails.car_name || booking.title || 'Car',
            carCode: carDetails.car_code || '',
            transmission: carDetails.transmission_type || '',
            image: carDetails.car_image || CarName,
            route: `${booking.pickup?.location_name || ''} → ${booking.dropoff?.location_name || ''}`,
            date: booking.booking_date || booking.pickup?.date_time || '',
            passengers: `${booking.passenger_count || 1} Adult${Number(booking.passenger_count) > 1 ? 's' : ''}`,
        }

        const PICKUP_DROPOFF = [
            { label: 'Pick-up', point: booking.pickup },
            { label: 'Drop-off', point: booking.dropoff },
        ]

        const PASSENGER = {
            name: `${pax.title || ''} ${pax.first_name || ''} ${pax.last_name || ''}`.trim()
                || contact.customer_name
                || '',
            addressLine: [pax.address, pax.city, pax.state, pax.pincode].filter(Boolean).join(', '),
        }

        const PAYMENT_DETAILS = [
            { label: 'Rental Amount', value: formatMoney(fee.rate_total_amount, fee.currency || booking.currency) },
            { label: 'Insurance', value: formatMoney(fee.insurance_amount, fee.currency || booking.currency) },
            { label: 'Extras', value: formatMoney(fee.extras_amount, fee.currency || booking.currency) },
            { label: 'Estimated Deposit', value: formatMoney(fee.estimated_deposit, fee.currency || booking.currency) },
            { label: 'Remaining Amount', value: formatMoney(fee.remaining_amount, fee.currency || booking.currency) },
        ]

        const totalPaid = (Number(fee.rate_total_amount) || 0)
            + (Number(fee.insurance_amount) || 0)
            + (Number(fee.extras_amount) || 0)

        return (
            <section className='commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <div className='row g-4'>
                        <div className='col-md-12'>
                            <div className='bookingConfirmInner'>
                                <div className='tripDocHeader' onClick={() => navigate(-1)}>
                                    <BackArrow />
                                    <span className='bsSectionTitle'>Car Booking Detail</span>
                                </div>
                                <div className='resultCard'>
                                    <div className='bookingConfirmMeta'>
                                        <div className='bookingConfirmMetaLabel'>Booking ID <strong>{CAR_BOOKING.bookingId}</strong></div>
                                        {status && (
                                            <span className={`carBookingStatusBadge ${carCancelled ? 'carBookingStatusBadge--cancelled' : ''}`}>{status}</span>
                                        )}
                                    </div>

                                    <img src={CAR_BOOKING.image} alt={CAR_BOOKING.title} className='carResultCard__image bookingCarTime' />

                                    <div className='carResultCard__title'>{CAR_BOOKING.title}{CAR_BOOKING.carCode ? ` (${CAR_BOOKING.carCode})` : ''}</div>
                                    <div className='carResultCard__subtitle'>{CAR_BOOKING.transmission}</div>

                                    <div className='bsFareDivider mt-4' />

                                    <div className='bsFareRow'>
                                        <span>Route:</span>
                                        <span>{CAR_BOOKING.route}</span>
                                    </div>
                                    <div className='bsFareRow'>
                                        <span>Date:</span>
                                        <DateTimeStack value={CAR_BOOKING.date} />
                                    </div>
                                    <div className='bsFareRow'>
                                        <span>Passengers:</span>
                                        <span>{CAR_BOOKING.passengers}</span>
                                    </div>

                                    <div className='bookingConfirmActions'>
                                        <button type='button' className='bookingDownloadBtn' onClick={handleDownloadInvoice} disabled={invoiceDownloading}>
                                            {invoiceDownloading ? 'Downloading...' : 'Download Invoice'}
                                        </button>
                                        {/* <button type='button' className='bookingShareBtn' aria-label="Share">
                                            <img src={Share} alt="" />
                                        </button> */}
                                    </div>

                                    <div className='bookingConfirmActions mt-2'>
                                        <button type='button' className='bookingDownloadBtn' onClick={handleDownloadTicket} disabled={ticketDownloading}>
                                            {ticketDownloading ? 'Downloading...' : 'Download Ticket'}
                                        </button>
                                        {canCancel && (
                                            <button type='button' className='bookingDownloadBtn tripDocCancelBtn' onClick={handleCancelBooking} disabled={cancelLoading}>
                                                {cancelLoading ? 'Cancelling...' : 'Cancel Booking'}
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Passenger Detail</div>
                                    <div className='bsBox'>
                                        <div className='bookingGuestCard'>
                                            <div className='bsFareRow'>
                                                <span>Passenger 1</span>
                                            </div>
                                            <div className='hotelCard__name'>{PASSENGER.name || '-'}</div>
                                            {PASSENGER.addressLine && (
                                                <div className='bsFareRow'>
                                                    <span>Address:</span>
                                                    <span>{PASSENGER.addressLine}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Pickup &amp; Drop-off Details</div>
                                    <div className='bsBox'>
                                        {PICKUP_DROPOFF.map((item, index) => (
                                            <div key={item.label}>
                                                {index > 0 && <div className='bsFareDivider' />}
                                                <div className='bsSectionTitle fareBaggageTitle'>{item.label}</div>
                                                <div className='bsFareRow'>
                                                    <span>Location:</span>
                                                    <span>{item.point?.location_name || '-'}</span>
                                                </div>
                                                <div className='bsFareRow'>
                                                    <span>Address:</span>
                                                    <span>{item.point?.address || '-'}</span>
                                                </div>
                                                <div className='bsFareRow'>
                                                    <span>Date &amp; Time:</span>
                                                    <DateTimeStack value={item.point?.date_time} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Contact Details</div>
                                    <div className='bsBox'>
                                        <div className='bsFareRow'><span>Full Name:</span><span>{PASSENGER.name || '—'}</span></div>
                                        <div className='bsFareRow'><span>Email ID:</span><span>{contact.email || '-'}</span></div>
                                        <div className='bsFareRow'><span>Phone Number:</span><span>{contact.phone || '-'}</span></div>
                                    </div>
                                </div>

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Payment Details</div>
                                    <div className='bsBox'>
                                        {PAYMENT_DETAILS.map((item, index) => (
                                            <div className='bsFareRow' key={index}>
                                                <span>{item.label}</span>
                                                <span>{item.value}</span>
                                            </div>
                                        ))}
                                        <div className='bsFareDivider' />
                                        <div className='bsFareRow bsFareRow--total'>
                                            <span>TOTAL AMOUNT</span>
                                            <span>{booking.amount_label || formatMoney(totalPaid, fee.currency || booking.currency)}</span>
                                        </div>
                                    </div>
                                </div>

                                <TripRatingSection bookingId={bookingId} />

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Important Information</div>
                                    <div className='bsBox'>
                                        <ul className='bsInfoList'>
                                            {CAR_IMPORTANT_INFO.map((info, index) => (
                                                <li key={index}>{info}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        )
    }

    // ---- Events branch ----
    if (isEventTrip) {
        const eventInfo = booking.event || {}
        const schedule = booking.schedule || {}
        const items = Array.isArray(booking.items) ? booking.items : []
        const payment = booking.payment_summary || {}
        const guests = Array.isArray(booking.guests) ? booking.guests : []
        const currency = payment.currency || 'INR'
        const status = (booking.status_label || booking.status || '').toUpperCase()
        const eventCancelledStatus = ['cancelled', 'refunded', 'failed'].includes(String(booking.status || '').toLowerCase())
        const scheduleDateTime = schedule.event_date
            ? new Date(`${schedule.event_date}T${schedule.start_time || '00:00:00'}`)
            : null
        const canCancelEvent = String(booking.status).toLowerCase() === 'confirmed'
            && (!scheduleDateTime || scheduleDateTime > new Date())

        return (
            <section className='commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <div className='row g-4'>
                        <div className='col-md-12'>
                            <div className='bookingConfirmInner'>
                                <div className='tripDocHeader' onClick={() => navigate(-1)}>
                                    <BackArrow />
                                    <span className='bsSectionTitle'>Event Detail</span>
                                </div>
                                <div className='resultCard'>
                                    <div className='bookingConfirmMeta'>
                                        <div className='bookingConfirmMetaLabel'>Booking ID <strong>#{booking.booking_id}</strong></div>
                                        {status && (
                                            <span className={`carBookingStatusBadge ${eventCancelledStatus ? 'carBookingStatusBadge--cancelled' : ''}`}>{status}</span>
                                        )}
                                    </div>
                                    {booking.confirmation_code ? (
                                        <div className='bookingConfirmMetaLabel mt-2'>Confirmation Code <strong>{booking.confirmation_code}</strong></div>
                                    ) : null}

                                    <div className='hotelCard__name mt-3'>{eventInfo.name || '—'}</div>

                                    <div className='bookingSegmentBox bookingInfoRow mt-3'>
                                        <div>
                                            <div className='bookingInfoLabel'>DATE</div>
                                            <div className='bookingInfoDate'>{formatBookingDate(schedule.event_date)}</div>
                                        </div>
                                        <div>
                                            <div className='bookingInfoLabel'>TIME</div>
                                            <div className='bookingInfoDate'>{schedule.start_time || '—'}</div>
                                        </div>
                                        <div className='text-end'>
                                            <div className='bookingInfoLabel'>TICKETS</div>
                                            <div className='bookingInfoDate'>
                                                {items.length ? items.map((item) => `${item.ticket_name} × ${item.quantity}`).join(', ') : '—'}
                                            </div>
                                        </div>
                                    </div>

                                    <div className='bookingConfirmActions'>
                                        <button type='button' className='bookingDownloadBtn' onClick={handleDownloadInvoice} disabled={invoiceDownloading}>
                                            {invoiceDownloading ? 'Downloading...' : 'Download Invoice'}
                                        </button>
                                    </div>

                                    <div className='bookingConfirmActions mt-2'>
                                        <button type='button' className='bookingDownloadBtn' onClick={handleDownloadTicket} disabled={ticketDownloading}>
                                            {ticketDownloading ? 'Downloading...' : 'Download Ticket'}
                                        </button>
                                        {canCancelEvent && (
                                            <button type='button' className='bookingDownloadBtn tripDocCancelBtn' onClick={handleCancelEventBooking} disabled={eventCancelLoading}>
                                                {eventCancelLoading ? 'Cancelling...' : 'Cancel Booking'}
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Guest Details</div>
                                    <div className='bsBox'>
                                        {guests.length ? guests.map((guest, index) => (
                                            <div className='bookingGuestCard' key={guest.id ?? index}>
                                                <div className='bookingPassengerRow'>
                                                    <span className='bookingPassengerType'>GUEST {index + 1}</span>
                                                    {guest.seat_no ? <span className='travellerPassengerType'>Seat {guest.seat_no}</span> : null}
                                                </div>
                                                <div className='bookingGuestGrid'>
                                                    <div>
                                                        <div className='bookingConfirmMetaLabel'>Name:</div>
                                                        <div className='bookingPassengerDetail'>{[guest.first_name, guest.last_name].filter(Boolean).join(' ') || '—'}</div>
                                                    </div>
                                                    <div>
                                                        <div className='bookingConfirmMetaLabel'>Email:</div>
                                                        <div className='bookingPassengerDetail'>{guest.email || '—'}</div>
                                                    </div>
                                                    <div>
                                                        <div className='bookingConfirmMetaLabel'>Phone Number:</div>
                                                        <div className='bookingPassengerDetail'>{guest.phone_number || '—'}</div>
                                                    </div>
                                                    <div>
                                                        <div className='bookingConfirmMetaLabel'>Ticket No:</div>
                                                        <div className='bookingPassengerDetail'>{guest.ticket_no || '—'}</div>
                                                    </div>
                                                </div>
                                                {String(booking.status).toLowerCase() === 'confirmed' && (
                                                    <div className='bookingConfirmActions mt-2'>
                                                        {guest.checkin ? (
                                                            guest.checkout ? (
                                                                <span className='bsFareNote'>Checked out {formatBookingDateTime(guest.checkout)}</span>
                                                            ) : (
                                                                <>
                                                                    <span className='bsFareNote'>Checked in {formatBookingDateTime(guest.checkin)}</span>
                                                                    <button
                                                                        type='button'
                                                                        className='bookingDownloadBtn'
                                                                        onClick={() => handleGuestCheckout(guest.id)}
                                                                        disabled={guestActionLoading === guest.id}
                                                                    >
                                                                        {guestActionLoading === guest.id ? 'Please wait...' : 'Check Out'}
                                                                    </button>
                                                                </>
                                                            )
                                                        ) : (
                                                            <button
                                                                type='button'
                                                                className='bookingDownloadBtn'
                                                                onClick={() => handleGuestCheckin(guest.id)}
                                                                disabled={guestActionLoading === guest.id}
                                                            >
                                                                {guestActionLoading === guest.id ? 'Please wait...' : 'Check In'}
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )) : (
                                            <p className='bookingConfirmSentText'>Guest details unavailable</p>
                                        )}
                                    </div>
                                </div>

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Payment Details</div>
                                    <div className='bsBox'>
                                        <div className='bsFareRow'>
                                            <span>Net Amount</span>
                                            <span>{formatMoney(payment.net_amount, currency)}</span>
                                        </div>
                                        <div className='bsFareRow'>
                                            <span>Taxes &amp; Fees</span>
                                            <span>{formatMoney(payment.taxes_and_fees, currency)}</span>
                                        </div>
                                        <div className='bsFareRow'>
                                            <span>Convenience Fee</span>
                                            <span>{formatMoney(payment.convenience_fee, currency)}</span>
                                        </div>
                                        {Number(payment.discount_amount) > 0 && (
                                            <div className='bsFareRow bookingDiscountRow'>
                                                <span>Discount</span>
                                                <span>- {formatMoney(payment.discount_amount, currency)}</span>
                                            </div>
                                        )}
                                        <div className='bsFareDivider' />
                                        <div className='bsFareRow bsFareRow--total'>
                                            <span>TOTAL AMOUNT</span>
                                            <span>{formatMoney(payment.total_amount, currency)}</span>
                                        </div>
                                        <div className='bsFareDivider' />
                                        <div className='bsFareRow'>
                                            <span>Booked On</span>
                                            <span>{formatBookingDateTime(booking.created_at)}</span>
                                        </div>
                                    </div>
                                </div>

                                <TripRatingSection bookingId={bookingId} />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        )
    }

    // ---- Venue branch ----
    if (isVenueTrip) {
        const eventDetails = booking.event_details || {}
        const customer = booking.customer || {}
        const vendorContact = booking.vendor_contact || {}
        const proposal = booking.proposal_details || {}
        const payment = booking.payment_summary || {}
        const paymentSchedule = Array.isArray(payment.payment_schedule) ? payment.payment_schedule : []
        const whatsNext = Array.isArray(booking.whats_next) ? booking.whats_next : []
        const currency = eventDetails.currency || payment.currency || 'INR'
        // Needed to route "Pay Balance" to /venue-payment/{proposal_id} — this
        // generic trip-detail endpoint's exact field name for it is UNCONFIRMED
        // (this app has had real proposal-id-vs-quotation-id mixups before in
        // this exact vertical), so every plausible field is tried and the
        // button is simply hidden if none resolve, rather than risk a broken
        // link. Verify against a real venue trip-detail response and fix.
        const proposalId = proposal.id || proposal.proposal_id || booking.proposal_id || booking.rfp_id || null
        const nextPendingSchedule = paymentSchedule.find((item) => item.status !== 'paid') || null
        const balanceDueAmount = Number(payment.balance_due) || 0
        const remainingStepsCount = paymentSchedule.filter((item) => item.status !== 'paid').length
        // "Pay Full Amount" is only a meaningfully different choice from "Pay
        // Next Installment" when more than one installment is still unpaid.
        const canPayFull = remainingStepsCount > 1

        // Confirmed via Swagger 2026-09-22: the first installment is paid at
        // accept — every installment after that goes through this endpoint,
        // not a repeat of the accept flow. `pay_full` toggles between just
        // the next installment and every remaining one in a single charge.
        const handlePayInstallment = (payFull) => {
            if (!proposalId || venuePayNextLoading) return
            setVenuePayNextLoading(true)
            service
                .post(endpoints.venues.paymentRemainingInstallments, {
                    quotation_id: Number(proposalId),
                    gateway: 'stripe',
                    pay_full: payFull,
                })
                .then(({ data }) => {
                    const body = data?.data ?? data ?? {}
                    // Confirmed live 2026-09-22: the Stripe fields are nested
                    // under `checkout`, not top-level like the accept response.
                    const checkout = body.checkout || {}
                    if (!checkout.client_secret) throw new Error('Payment session missing from the server response.')
                    navigate(`/venue-payment/${proposalId}`, {
                        state: {
                            bookingId: checkout.booking_id ?? (booking.id || booking.booking_id),
                            clientSecret: checkout.client_secret,
                            paymentIntentId: checkout.payment_intent_id || '',
                            publishableKey: checkout.publishable_key || '',
                            gateway: checkout.gateway || 'stripe',
                            payAmount: body.amount ?? checkout.payable_amount ?? null,
                            payCurrency: body.currency || checkout.currency || currency,
                            nextStep: payFull
                                ? { title: 'Remaining Balance', amount: '', dueText: '' }
                                : (nextPendingSchedule ? {
                                    title: nextPendingSchedule.title,
                                    amount: formatMoney(nextPendingSchedule.amount, currency),
                                    dueText: nextPendingSchedule.due_text,
                                } : null),
                        },
                    })
                })
                .catch((err) => {
                    showToast(err?.message || 'Could not start the payment. Please try again.', 'error')
                })
                .finally(() => setVenuePayNextLoading(false))
        }

        return (
            <section className='commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <div className='row g-4'>
                        <div className='col-md-12'>
                            <div className='bookingConfirmInner'>
                                <div className='tripDocHeader' onClick={() => navigate(-1)}>
                                    <BackArrow />
                                    <span className='bsSectionTitle'>Venue Booking Detail</span>
                                </div>
                                <div className='resultCard'>
                                    <div className='bookingConfirmMeta'>
                                        <div className='bookingConfirmMetaLabel'>Booking ID <strong>{booking.booking_id || `#${booking.id}`}</strong></div>
                                        {booking.status_display && (
                                            <span className='carBookingStatusBadge'>{booking.status_display}</span>
                                        )}
                                    </div>
                                    {booking.payment_status && (
                                        <div className='bookingConfirmMetaLabel mt-2'>Payment Status <strong>{booking.payment_status}</strong></div>
                                    )}

                                    <div className='hotelCard__name mt-3'>{naToDash(eventDetails.venue_name)}</div>
                                    <div className='hotelCard__locationNote'>{naToDash(eventDetails.venue_address)}</div>

                                    <div className='bookingSegmentBox bookingInfoRow mt-3'>
                                        <div>
                                            <div className='bookingInfoLabel'>EVENT TYPE</div>
                                            <div className='bookingInfoDate'>{naToDash(eventDetails.event_type)}</div>
                                        </div>
                                        <div>
                                            <div className='bookingInfoLabel'>DATE</div>
                                            <div className='bookingInfoDate'>{eventDetails.event_date && eventDetails.event_date !== 'N/A' ? formatBookingDate(eventDetails.event_date) : '—'}</div>
                                        </div>
                                        <div className='text-end'>
                                            <div className='bookingInfoLabel'>TIME</div>
                                            <div className='bookingInfoDate'>{naToDash(eventDetails.time_slot)}</div>
                                        </div>
                                    </div>

                                    <div className='bsFareRow mt-2'>
                                        <span>Guest Count</span>
                                        <span>{eventDetails.guest_count || '—'}</span>
                                    </div>
                                    {eventDetails.special_requirements && eventDetails.special_requirements !== 'None' && (
                                        <div className='bsFareRow'>
                                            <span>Special Requirements</span>
                                            <span>{eventDetails.special_requirements}</span>
                                        </div>
                                    )}

                                    <div className='bookingConfirmActions mt-3'>
                                        <button type='button' className='bookingDownloadBtn' onClick={handleDownloadInvoice} disabled={invoiceDownloading}>
                                            {invoiceDownloading ? 'Downloading...' : 'Download Invoice'}
                                        </button>
                                        <button type='button' className='bookingDownloadBtn' onClick={handleDownloadTicket} disabled={ticketDownloading}>
                                            {ticketDownloading ? 'Downloading...' : 'Download Ticket'}
                                        </button>
                                    </div>
                                </div>

                                {proposal.status_display && (
                                    <div className='bsSection'>
                                        <div className='bsSectionTitle'>Proposal</div>
                                        <div className='bsBox'>
                                            <div className='bsFareRow'><span>Status</span><span>{proposal.status_display}</span></div>
                                            {proposal.negotiation_status_display && (
                                                <div className='bsFareRow'><span>Negotiation</span><span>{proposal.negotiation_status_display}</span></div>
                                            )}
                                            {(proposal.budget_min || proposal.budget_max) ? (
                                                <div className='bsFareRow'>
                                                    <span>Budget Range</span>
                                                    <span>{formatMoney(proposal.budget_min, proposal.currency || currency)} – {formatMoney(proposal.budget_max, proposal.currency || currency)}</span>
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                )}

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Contact Details</div>
                                    <div className='bsBox'>
                                        <div className='bsFareRow'><span>Full Name:</span><span>{customer.name || '—'}</span></div>
                                        <div className='bsFareRow'><span>Email ID:</span><span>{customer.email || '—'}</span></div>
                                        <div className='bsFareRow'><span>Phone Number:</span><span>{customer.phone || '—'}</span></div>
                                    </div>
                                </div>

                                {(vendorContact.contact_person || vendorContact.email || vendorContact.phone_number) && (
                                    <div className='bsSection'>
                                        <div className='bsSectionTitle'>Vendor Contact</div>
                                        <div className='bsBox'>
                                            <div className='bsFareRow'><span>Contact Person:</span><span>{vendorContact.contact_person || '—'}</span></div>
                                            <div className='bsFareRow'><span>Email:</span><span>{vendorContact.email || '—'}</span></div>
                                            <div className='bsFareRow'><span>Phone:</span><span>{vendorContact.phone_number || '—'}</span></div>
                                        </div>
                                    </div>
                                )}

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Payment Summary</div>
                                    <div className='bsBox'>
                                        <div className='bsFareRow'><span>Total Booking Amount</span><span>{formatMoney(payment.total_booking_amount, currency)}</span></div>
                                        <div className='bsFareRow'><span>Amount Paid</span><span>{formatMoney(payment.amount_paid, currency)}</span></div>
                                        <div className='bsFareDivider' />
                                        <div className='bsFareRow bsFareRow--total'><span>Balance Due</span><span>{formatMoney(payment.balance_due, currency)}</span></div>

                                        {paymentSchedule.length > 0 && (
                                            <>
                                                <div className='bsFareDivider' />
                                                {paymentSchedule.map((item) => {
                                                    const isPaid = item.status === 'paid'
                                                    const methodLabel = item.payment_method ? (PAYMENT_METHOD_LABELS[item.payment_method] || item.payment_method) : null
                                                    return (
                                                        <div className='venueScheduleRow' key={item.step}>
                                                            <div className='venueScheduleRow__label'>
                                                                <span className={`venueScheduleRow__step ${isPaid ? '' : 'venueScheduleRow__step--inactive'}`}>{item.step}</span>
                                                                <div>
                                                                    <div className='venueScheduleRow__title'>{item.title}</div>
                                                                    <div className={`venueScheduleRow__due ${isPaid ? 'venueScheduleRow__due--paid' : ''}`}>
                                                                        {isPaid
                                                                            ? `Paid ${formatBookingDateTime(item.paid_at)}${methodLabel ? ` via ${methodLabel}` : ''}`
                                                                            : item.due_text}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <span className='venueScheduleRow__value'>{formatMoney(item.amount, currency)}</span>
                                                        </div>
                                                    )
                                                })}
                                            </>
                                        )}
                                    </div>

                                    {proposalId && balanceDueAmount > 0 && (
                                        <>
                                            <button
                                                type='button'
                                                className='commonBtn w-100 mt-3'
                                                disabled={venuePayNextLoading}
                                                onClick={() => handlePayInstallment(false)}
                                            >
                                                {venuePayNextLoading
                                                    ? 'Please wait...'
                                                    : nextPendingSchedule
                                                        ? `Pay Next Installment (${formatMoney(nextPendingSchedule.amount, currency)})`
                                                        : `Pay Balance (${formatMoney(balanceDueAmount, currency)})`}
                                            </button>
                                            {canPayFull && (
                                                <button
                                                    type='button'
                                                    className='venueModalBtn w-100 mt-2'
                                                    disabled={venuePayNextLoading}
                                                    onClick={() => handlePayInstallment(true)}
                                                >
                                                    {venuePayNextLoading ? 'Please wait...' : `Pay Full Amount (${formatMoney(balanceDueAmount, currency)})`}
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>

                                {whatsNext.length > 0 && (
                                    <div className='bsSection'>
                                        <div className='bsSectionTitle'>What&apos;s Next</div>
                                        <div className='bsBox'>
                                            <ul className='bsInfoList'>
                                                {whatsNext.map((item, index) => (
                                                    <li key={index}>{item}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                )}

                                <TripRatingSection bookingId={bookingId} />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        )
    }

    // ---- Hotel branch ----
    if (isHotelTrip) {
        const rating = Math.max(0, Math.min(5, Math.round(Number(booking.rating) || 0)))
        const locationLabel = [booking.address, booking.city, booking.country].filter(Boolean).join(', ') || '—'
        const guestRooms = Array.isArray(booking.guest_room) ? booking.guest_room : []
        const roomsCount = guestRooms.length || 1
        const totalAdults = guestRooms.reduce((sum, r) => sum + (Number(r.adult) || 0), 0)
        const totalChildren = guestRooms.reduce((sum, r) => sum + (Number(r.child) || 0), 0)
        const guestsRoomsLabel = [
            `${totalAdults} Adult${totalAdults > 1 ? 's' : ''}`,
            totalChildren > 0 ? `${totalChildren} Child${totalChildren > 1 ? 'ren' : ''}` : null,
            `${roomsCount} Room${roomsCount > 1 ? 's' : ''}`,
        ].filter(Boolean).join(' • ')
        const firstDate = (...values) => values.find((value) => value != null && String(value).trim() !== '') || ''
        const checkIn = firstDate(
            booking.check_in,
            booking.checkin,
            booking.check_in_date,
            booking.checkIn,
            booking.stay?.check_in,
            booking.stay?.checkin,
        )
        const checkOut = firstDate(
            booking.check_out,
            booking.checkout,
            booking.check_out_date,
            booking.checkOut,
            booking.stay?.check_out,
            booking.stay?.checkout,
        )
        const nights = Number(booking.nights) || daysBetween(checkIn, checkOut) || 1

        const guestsByRoom = booking.guests || {}
        const roomSections = (guestRooms.length
            ? guestRooms
            : Object.keys(guestsByRoom).sort((a, b) => Number(a) - Number(b)).map((key) => ({ room: key }))
        ).map((roomMeta, index) => {
            const roomKey = String(roomMeta.room ?? roomMeta.room_no ?? (index + 1))
            const roomGuests = guestsByRoom[roomKey] || guestsByRoom[String(index + 1)] || {}
            const adults = Array.isArray(roomGuests.adult) ? roomGuests.adult : []
            const children = Array.isArray(roomGuests.child) ? roomGuests.child : []
            return {
                roomKey,
                roomNo: Number(roomKey) || index + 1,
                roomType: roomMeta.room_type || roomMeta.roomType || booking.room?.name || booking.room?.room_type || '',
                cancellationPolicy: roomMeta.cancellation_policy || roomMeta.cancellationPolicy || '',
                adultCount: Number(roomMeta.adult) || adults.length,
                childCount: Number(roomMeta.child) || children.length,
                guests: [
                    ...adults.map((guest) => ({ ...guest, guestKind: 'Adult' })),
                    ...children.map((guest) => ({ ...guest, guestKind: 'Child' })),
                ],
            }
        })
        const allGuests = roomSections.flatMap((section) => section.guests)

        const payment = booking.payment_summary || {}
        const currency = payment.currency || 'INR'
        const room = booking.room || {}
        const roomGuestCount = totalAdults + totalChildren
        const heroImage = (Array.isArray(booking.images) && booking.images[0])
            || (Array.isArray(room.images) && room.images[0])
            || HotelSample
        const hero = heroStatusMeta(booking.status || 'confirmed')

        return (
            <section className='commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <div className='row g-4'>
                        <div className='col-md-12'>
                            <div className='bookingConfirmInner'>
                                <div className='tripDocHeader' onClick={() => navigate(-1)}>
                                    <BackArrow />
                                    <span className='bsSectionTitle'>Hotel Detail</span>
                                </div>
                                <div className={`bookingTextConfirm ${hero.kind}`} style={{ marginBottom: 16 }}>
                                    <div className='bookingConfirmStatusIcon' aria-hidden="true">{hero.icon}</div>
                                    <div className='bookingHead commonGredientText'>
                                        {booking.status_label || hero.title}
                                    </div>
                                    <p>{booking.hotel_name || 'Hotel booking'}</p>
                                </div>

                                <div className='resultCard'>
                                    <div className='bookingConfirmMetaLabel'>Booking ID <strong>#{booking.booking_id}</strong></div>

                                    <div className='hotelCard__topRow mt-3'>
                                        <div>
                                            <div className='hotelCard__name'>{booking.hotel_name || 'Hotel'}</div>
                                            <div className='hotelCard__location mt-2'>
                                                <img src={Location} alt="" />
                                                <div className='hotelCard__locationName'>{locationLabel}</div>
                                            </div>
                                            {(booking.reference_number || booking.referenceNumber) ? (
                                                <div className='hotelTripReference mt-2'>
                                                    Reference No. {booking.reference_number || booking.referenceNumber}
                                                </div>
                                            ) : null}
                                        </div>
                                        <div className='hotelCard__stars'>
                                            {Array.from({ length: rating }).map((_, index) => <GoldStar key={index} />)}
                                        </div>
                                    </div>

                                    <div className='bookingSegmentBox bookingInfoRow'>
                                        <div>
                                            <div className='bookingInfoLabel'>CHECK-IN</div>
                                            <div className='bookingInfoDate'>{formatBookingDate(checkIn)}</div>
                                        </div>
                                        <div className='bookingInfoNights'>— {nights} Night{nights > 1 ? 's' : ''} —</div>
                                        <div className='text-end'>
                                            <div className='bookingInfoLabel'>CHECK-OUT</div>
                                            <div className='bookingInfoDate'>{formatBookingDate(checkOut)}</div>
                                        </div>
                                    </div>

                                    <div className='bsFareRow mt-2'>
                                        <span>GUEST &amp; ROOMS</span>
                                        <span>{guestsRoomsLabel}</span>
                                    </div>

                                    <div className='bsSectionTitle mt-3'>Guests</div>
                                    <ul className='paymentTravellerList'>
                                        {roomSections.some((section) => section.guests.length) ? roomSections.map((section) => (
                                            <li key={`summary-room-${section.roomKey}`}>
                                                <strong>Room {section.roomNo}</strong>
                                                {section.guests.length
                                                    ? ` — ${section.guests.map((guest) => guestName(guest)).join(', ')}`
                                                    : ' — No guests listed'}
                                            </li>
                                        )) : (
                                            <li>Guest details unavailable</li>
                                        )}
                                    </ul>
                                </div>

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Room &amp; Guest Details</div>
                                    {roomSections.length ? roomSections.map((section) => (
                                        <div className='bsBox hotelTripRoomCard' key={`room-section-${section.roomKey}`}>
                                            <div className='hotelTripRoomCard__head'>
                                                <div className='hotelCard__name'>Room {section.roomNo}</div>
                                                <div className='roomBookingSpecs'>
                                                    <span>
                                                        <img src={Guest} alt="" />
                                                        {' '}
                                                        {(section.adultCount || 0) + (section.childCount || 0) || section.guests.length || 1}
                                                        {' '}
                                                        Guest{(section.adultCount + section.childCount || section.guests.length) > 1 ? 's' : ''}
                                                    </span>
                                                    {section.adultCount ? <span>{section.adultCount} Adult{section.adultCount > 1 ? 's' : ''}</span> : null}
                                                    {section.childCount ? <span>{section.childCount} Child{section.childCount > 1 ? 'ren' : ''}</span> : null}
                                                </div>
                                            </div>

                                            {section.roomType ? (
                                                <div className='hotelTripRoomCard__row'>
                                                    <div className='bookingConfirmMetaLabel'>Room Type</div>
                                                    <div className='bookingPassengerDetail'>{section.roomType}</div>
                                                </div>
                                            ) : null}

                                            {section.cancellationPolicy ? (
                                                <div className='hotelTripRoomCard__row'>
                                                    <div className='bookingConfirmMetaLabel'>Cancellation Policy</div>
                                                    <div className='bookingPassengerDetail'>{section.cancellationPolicy}</div>
                                                </div>
                                            ) : null}

                                            <div className='hotelTripRoomCard__guestsTitle'>Guests</div>
                                            {section.guests.length ? section.guests.map((guest, guestIndex) => (
                                                <div className='bookingGuestCard' key={`${section.roomKey}-guest-${guestIndex}`}>
                                                    <span className='bookingPassengerType'>
                                                        {guest.guestKind || 'GUEST'} {guestIndex + 1}
                                                    </span>
                                                    <div className='bookingGuestGrid'>
                                                        <div>
                                                            <div className='bookingConfirmMetaLabel'>Guest Name:</div>
                                                            <div className='bookingPassengerDetail'>{guestName(guest)}</div>
                                                        </div>
                                                        <div>
                                                            <div className='bookingConfirmMetaLabel'>Email:</div>
                                                            <div className='bookingPassengerDetail'>{guest.email || '—'}</div>
                                                        </div>
                                                        <div>
                                                            <div className='bookingConfirmMetaLabel'>Phone Number:</div>
                                                            <div className='bookingPassengerDetail'>{[guest.phone_code, guest.phone].filter(Boolean).join(' ') || '—'}</div>
                                                        </div>
                                                        <div>
                                                            <div className='bookingConfirmMetaLabel'>Age &amp; Gender:</div>
                                                            <div className='bookingPassengerDetail'>
                                                                {[guest.age != null ? `${guest.age} yrs` : null, guest.dob, guest.gender]
                                                                    .filter(Boolean)
                                                                    .join(' • ') || '—'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )) : (
                                                <p className='bookingConfirmSentText'>No guests listed for this room</p>
                                            )}
                                        </div>
                                    )) : (
                                        <div className='bsBox'>
                                            <div className='roomBookingTop'>
                                                <span className='roomBookingImage'>
                                                    <img src={heroImage} alt={room.name || 'Room'} />
                                                </span>
                                                <div>
                                                    <div className='hotelCard__name'>{room.name || 'Room'}</div>
                                                    <div className='roomBookingSpecs'>
                                                        <span><img src={Guest} alt="" /> {roomGuestCount || 1} Guest{roomGuestCount > 1 ? 's' : ''}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            {allGuests.length ? allGuests.map((guest, index) => (
                                                <div className='bookingGuestCard' key={index}>
                                                    <span className='bookingPassengerType'>GUEST {index + 1}</span>
                                                    <div className='bookingGuestGrid'>
                                                        <div>
                                                            <div className='bookingConfirmMetaLabel'>Guest Name:</div>
                                                            <div className='bookingPassengerDetail'>{guestName(guest)}</div>
                                                        </div>
                                                        <div>
                                                            <div className='bookingConfirmMetaLabel'>Email:</div>
                                                            <div className='bookingPassengerDetail'>{guest.email || '—'}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )) : (
                                                <p className='bookingConfirmSentText'>Guest details unavailable</p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Payment Details</div>
                                    <div className='bsBox'>
                                        <div className='bsFareRow'>
                                            <span>Room Price</span>
                                            <span>{formatMoney(payment.room_price, currency)}</span>
                                        </div>
                                        {(payment.taxes || []).map((tax, index) => (
                                            <div className='bsFareRow' key={index}>
                                                <span>{tax.name || 'Tax'}{tax.type === 'percentage' && tax.rate != null ? ` (${tax.rate}%)` : ''}</span>
                                                <span>{formatMoney(tax.amount, currency)}</span>
                                            </div>
                                        ))}
                                        <div className='bsFareRow'>
                                            <span>Convenience Fee</span>
                                            <span>{formatMoney(payment.convenience_fee, currency)}</span>
                                        </div>
                                        {Number(payment.discount) > 0 && (
                                            <div className='bsFareRow bookingDiscountRow'>
                                                <span>Discount Applied</span>
                                                <span>- {formatMoney(payment.discount, currency)}</span>
                                            </div>
                                        )}
                                        <div className='bsFareDivider' />
                                        <div className='bsFareRow bsFareRow--total'>
                                            <span>TOTAL AMOUNT</span>
                                            <span>{formatMoney(payment.total, currency)}</span>
                                        </div>
                                        <div className='bsFareDivider' />
                                        {booking.payment_method && (
                                            <div className='bsFareRow'>
                                                <span>Payment Method</span>
                                                <span>{booking.payment_method}</span>
                                            </div>
                                        )}
                                        <div className='bsFareRow'>
                                            <span>Transaction ID</span>
                                            <span>{booking.transaction_id || '—'}</span>
                                        </div>
                                        <div className='bsFareRow'>
                                            <span>Payment Date</span>
                                            <span>{formatBookingDateTime(booking.transaction_date)}</span>
                                        </div>
                                    </div>
                                </div>

                                <TripRatingSection bookingId={bookingId} />

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Important Information</div>
                                    <div className='bsBox'>
                                        <ul className='bsInfoList'>
                                            {HOTEL_IMPORTANT_INFO.map((info, index) => (
                                                <li key={index}>{info}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        )
    }

    // ---- Holiday package branch ----
    if (isPackageTrip) {
        const currency = booking.currency || booking.fare_breakup?.currency || 'INR'
        const fare = booking.fare_breakup || {}
        const contact = booking.contact || {}
        const adults = Array.isArray(booking.guests?.adults) ? booking.guests.adults : []
        const children = Array.isArray(booking.guests?.children) ? booking.guests.children : []
        const travelers = [
            ...adults.map((guest) => ({ ...guest, kind: 'Adult' })),
            ...children.map((guest) => ({ ...guest, kind: 'Child' })),
        ]
        const contactLine = [contact.address, contact.city, contact.state, contact.country, contact.pincode].filter(Boolean).join(', ')
        const hero = heroStatusMeta(booking.status || 'confirmed')

        return (
            <section className='commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <div className='row g-4'>
                        <div className='col-md-12'>
                            <div className='bookingConfirmInner'>
                                <div className='tripDocHeader' onClick={() => navigate(-1)}>
                                    <BackArrow />
                                    <span className='bsSectionTitle'>Package Detail</span>
                                </div>
                                <div className={`bookingTextConfirm ${hero.kind}`} style={{ marginBottom: 16 }}>
                                    <div className='bookingConfirmStatusIcon' aria-hidden="true">{hero.icon}</div>
                                    <div className='bookingHead commonGredientText'>
                                        {booking.status_label || hero.title}
                                    </div>
                                    <p>{booking.package_name || 'Holiday package'}</p>
                                </div>

                                <div className='resultCard'>
                                    <div className='bookingConfirmMetaLabel'>Booking ID <strong>#{booking.confirmation_code || booking.booking_id}</strong></div>

                                    <div className='hotelInfoCard__top mt-3'>
                                        <div>
                                            <div className='hotelInfoCard__name'>{booking.package_name || 'Holiday package'}</div>
                                            {booking.destination_routes ? (
                                                <div className='hotelCard__dateTime'>{booking.destination_routes}</div>
                                            ) : null}
                                        </div>
                                    </div>

                                    <div className='bookingSegmentBox bookingInfoRow'>
                                        <div>
                                            <div className='bookingInfoLabel'>TRAVEL DATE</div>
                                            <div className='bookingInfoDate'>{formatBookingDate(booking.travel_date)}</div>
                                        </div>
                                        <div>
                                            <div className='bookingInfoLabel'>DURATION</div>
                                            <div className='bookingInfoDate'>{booking.nights ?? '—'}N / {booking.days ?? '—'}D</div>
                                        </div>
                                        <div className='text-end'>
                                            <div className='bookingInfoLabel'>TRAVELERS</div>
                                            <div className='bookingInfoDate'>{booking.traveler_count ?? travelers.length}</div>
                                        </div>
                                    </div>

                                    <PackageCancelButton
                                        booking={booking}
                                        onCancelled={() => setBooking((prev) => ({ ...prev, status: 'cancelled', status_label: 'Cancelled', can_cancel: false }))}
                                    />
                                </div>

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Traveler Details</div>
                                    <div className='bsBox'>
                                        {travelers.length ? travelers.map((traveler, index) => (
                                            <div className='bookingGuestCard' key={index}>
                                                <span className='bookingPassengerType'>{traveler.kind.toUpperCase()} {index + 1}</span>
                                                <div className='bookingGuestGrid'>
                                                    <div>
                                                        <div className='bookingConfirmMetaLabel'>Full Name:</div>
                                                        <div className='bookingPassengerDetail'>{[traveler.first_name, traveler.last_name].filter(Boolean).join(' ') || '—'}</div>
                                                    </div>
                                                    <div>
                                                        <div className='bookingConfirmMetaLabel'>Email:</div>
                                                        <div className='bookingPassengerDetail'>{traveler.email || '—'}</div>
                                                    </div>
                                                    <div>
                                                        <div className='bookingConfirmMetaLabel'>Phone Number:</div>
                                                        <div className='bookingPassengerDetail'>{[traveler.phone_code, traveler.phone].filter(Boolean).join(' ') || '—'}</div>
                                                    </div>
                                                    <div>
                                                        <div className='bookingConfirmMetaLabel'>DOB &amp; Gender:</div>
                                                        <div className='bookingPassengerDetail'>
                                                            {[traveler.dob, genderLabel(traveler.gender)].filter(Boolean).join(' • ') || '—'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )) : (
                                            <p className='bsFareNote'>Traveler details unavailable</p>
                                        )}
                                    </div>
                                </div>

                                {(contactLine || (contact.gst_number && String(contact.gst_number).toUpperCase() !== 'NA')) ? (
                                    <div className='bsSection'>
                                        <div className='bsSectionTitle'>Contact Details</div>
                                        <div className='bsBox'>
                                            {contactLine ? (
                                                <div className='bsFareRow'>
                                                    <span>Address</span>
                                                    <span>{contactLine}</span>
                                                </div>
                                            ) : null}
                                            {contact.gst_number && String(contact.gst_number).toUpperCase() !== 'NA' ? (
                                                <div className='bsFareRow'>
                                                    <span>GST Number</span>
                                                    <span>{contact.gst_number}</span>
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                ) : null}

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Payment Details</div>
                                    <div className='bsBox'>
                                        <div className='bsFareRow'>
                                            <span>Package Fare</span>
                                            <span>{formatMoney(fare.amount ?? booking.amount, currency)}</span>
                                        </div>
                                        {Number(fare.discount_amount) > 0 && (
                                            <div className='bsFareRow bookingDiscountRow'>
                                                <span>Discount</span>
                                                <span>- {formatMoney(fare.discount_amount, currency)}</span>
                                            </div>
                                        )}
                                        {Number(fare.coupon_discount) > 0 && (
                                            <div className='bsFareRow bookingDiscountRow'>
                                                <span>Coupon Discount</span>
                                                <span>- {formatMoney(fare.coupon_discount, currency)}</span>
                                            </div>
                                        )}
                                        {Number(fare.convenience_fee) > 0 && (
                                            <div className='bsFareRow'>
                                                <span>Convenience Fee</span>
                                                <span>{formatMoney(fare.convenience_fee, currency)}</span>
                                            </div>
                                        )}
                                        <div className='bsFareDivider' />
                                        <div className='bsFareRow bsFareRow--total'>
                                            <span>TOTAL AMOUNT</span>
                                            <span>{formatMoney(fare.total_amount ?? booking.amount, currency)}</span>
                                        </div>
                                        {booking.payment_status ? (
                                            <>
                                                <div className='bsFareDivider' />
                                                <div className='bsFareRow'>
                                                    <span>Payment Status</span>
                                                    <span>{booking.payment_status}</span>
                                                </div>
                                            </>
                                        ) : null}
                                    </div>
                                </div>

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Important Information</div>
                                    <div className='bsBox'>
                                        <ul className='bsInfoList'>
                                            {PACKAGE_IMPORTANT_INFO.map((info) => (
                                                <li key={info}>{info}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        )
    }

    // ---- Flight branch (layout matches trips-preview.js renderFlightDetail) ----
    const { isRt, going, coming, info, returnInfo } = splitBookingLegs(booking)
    const contact = booking.contact_info || {}
    const travellers = Array.isArray(booking.travellers) ? booking.travellers : []
    const payment = booking.payment || {}
    const refund = booking.refund || null
    const coupon = booking.coupon || {}
    const lock = booking.lock || {}
    const from = booking.from_code || info.from_code || ''
    const to = booking.to_code || info.to_code || ''
    const hero = heroStatusMeta(booking)
    const fareLines = buildFlightFareLines(booking)
    const paymentMethod = payment.payment_method_label
        || payment.payment_method
        || (payment.gateway ? String(payment.gateway).replace(/_/g, ' ') : '')
        || ''
    const heroTitle = tripHeroTitle(booking)
    const issuing = isTicketInProgress(booking)
    const title = booking.title || booking.airline_name || info.airline_name || ''
    const subtitle = booking.subtitle || ''
    const locationLabel = booking.location || booking.route_label || (from && to ? `${from} → ${to}` : '')
    const pnr = booking.pnr || booking.confirmation_code || (issuing ? 'Issuing…' : '')
    const contactName = [contact.Title, contact.FName, contact.LName].filter(Boolean).join(' ')
    const failureNote = String(booking.failure_reason || '').trim()
    const providerBookingId = String(booking.provider_booking_id || '').trim()
    const goingRows = going.length ? going : (Array.isArray(booking.segments) ? booking.segments : [])
    const comingRows = coming || []
    const hasItinerary = goingRows.length > 0 || comingRows.length > 0

    return (
        <section className='commonTopPadding commonBottomPadding'>
            <div className='container'>
                <div className='tripDocHeader' onClick={() => navigate(-1)}>
                    <BackArrow />
                    <span className='bsSectionTitle'>Flight Detail</span>
                </div>
                <div className='confirm-screen trips-confirm'>
                    <div className={`confirm-hero ${hero.kind}`}>
                        <div className='confirm-hero-check' aria-hidden="true">{hero.icon}</div>
                        <h2>{heroTitle}</h2>
                        {issuing ? (
                            <p>Payment is complete. PNR and e-ticket will appear here when the airline confirms.</p>
                        ) : null}
                        {isRt ? (
                            <span className='trips-rt-chip-hero'>{booking.trip_type_label || 'Round-trip'}</span>
                        ) : null}
                        {title ? <p className='trips-detail-title'>{title}</p> : null}
                        {subtitle ? <p className='trips-detail-sub'>{subtitle}</p> : null}
                        {locationLabel ? <p>{locationLabel}</p> : null}
                        {(booking.from_name || booking.to_name) ? (
                            <p className='trips-hero-airports'>
                                {[booking.from_name, booking.to_name].filter(Boolean).join(' → ')}
                            </p>
                        ) : null}
                        {booking.date_label ? <p className='trips-hero-rt'>{booking.date_label}</p> : null}
                    </div>

                    <h3 className='confirm-section-title'>{booking.is_price_lock ? 'Locked flight' : 'Flight details'}</h3>
                    {hasItinerary ? (
                        isRt ? (
                            <>
                                <FlightLegPanel
                                    heading="Going"
                                    dateLabel={booking.onward_date_label || ''}
                                    rows={goingRows}
                                    booking={booking}
                                    startIndex={0}
                                    extraInfo={info}
                                />
                                <FlightLegPanel
                                    heading="Coming back"
                                    dateLabel={booking.return_date_label || ''}
                                    rows={comingRows}
                                    booking={booking}
                                    startIndex={goingRows.length}
                                    extraInfo={returnInfo || {}}
                                />
                            </>
                        ) : (
                            <FlightLegPanel
                                heading="Flight"
                                dateLabel={booking.onward_date_label || booking.date_label || ''}
                                rows={goingRows}
                                booking={booking}
                                startIndex={0}
                                extraInfo={info}
                            />
                        )
                    ) : (
                        <article className='confirm-card'>
                            <ul className='confirm-dl-rows'>
                                <DlRow label="Route" value={locationLabel || '—'} />
                                <DlRow label="Airline" value={title || '—'} />
                                <DlRow label="Flights" value={subtitle || '—'} />
                                <DlRow label="Date" value={booking.date_label || booking.onward_date || '—'} />
                            </ul>
                        </article>
                    )}
                    <article className='confirm-card'>
                        <ul className='confirm-dl-rows'>
                            <DlRow
                                label={isRt ? 'Going' : 'Date'}
                                value={booking.onward_date_label || booking.date_label || booking.onward_date}
                            />
                            {isRt && !isPlaceholderDate(booking.return_date) ? (
                                <DlRow label="Coming back" value={booking.return_date_label || booking.return_date} />
                            ) : null}
                            <DlRow label="PNR" value={pnr} />
                            <DlRow label="Ticket status" value={ticketStatusRow(booking)} />
                            <DlRow label="Travellers" value={booking.traveller_summary} />
                            {booking.web_checkin_url ? (
                                <DlRow label="Web check-in">
                                    <a href={booking.web_checkin_url} target='_blank' rel='noopener noreferrer'>Open</a>
                                </DlRow>
                            ) : null}
                            <DlRow label="Airline contact" value={booking.airline_contact} />
                            {failureNote && failureNote !== heroTitle ? (
                                <DlRow label="Note" value={failureNote} />
                            ) : null}
                        </ul>
                    </article>

                    {booking.is_price_lock ? (
                        <>
                            <h3 className='confirm-section-title'>Price lock</h3>
                            <article className='confirm-card'>
                                <ul className='confirm-dl-rows'>
                                    <DlRow label="Lock status" value={lock.status} />
                                    <DlRow label="Lock fee" value={lock.fee_label || (lock.fee ? `${lock.currency_symbol || ''}${lock.fee}` : '')} />
                                    <DlRow label="Expires" value={lock.expires_at} />
                                    <DlRow label="Duration (hours)" value={lock.duration_hours} />
                                </ul>
                            </article>
                        </>
                    ) : null}

                    <h3 className='confirm-section-title'>Contact Details</h3>
                    <article className='confirm-card'>
                        <ul className='confirm-dl-rows'>
                            <DlRow label="Full Name" value={contactName} />
                            <DlRow label="Email ID" value={contact.Email} />
                            <DlRow label="Phone Number" value={contact.Mobile || contact.Phone} />
                        </ul>
                    </article>

                    <h3 className='confirm-section-title'>Passenger Details</h3>
                    <article className='confirm-card'>
                        <ul className='confirm-pax-list'>
                            {travellers.length ? travellers.map((t, index) => {
                                const name = [t.Title, t.FName || t.first_name, t.LName || t.last_name].filter(Boolean).join(' ') || `Passenger ${index + 1}`
                                const bits = [
                                    t.Age != null && t.Age !== '' ? String(t.Age) : (t.age || ''),
                                    genderLabel(t.Gender || t.gender),
                                    t.TicketNo ? `Ticket ${t.TicketNo}` : '',
                                ].filter(Boolean).join(' | ')
                                return (
                                    <li key={index}>
                                        <div>
                                            <strong>Passenger {index + 1}</strong>
                                            <em>{name}{bits ? ` | ${bits}` : ''}</em>
                                        </div>
                                        <span className='confirm-pax-badge'>{ptcLabel(t.PTC || t.pax_type)}</span>
                                    </li>
                                )
                            }) : (
                                <li><div><strong>{booking.traveller_summary || '—'}</strong></div></li>
                            )}
                        </ul>
                    </article>

                    <h3 className='confirm-section-title'>Payment Details</h3>
                    <article className='confirm-card'>
                        <ul className='confirm-breakup'>
                            {fareLines.map((line, index) => (
                                <li
                                    key={`${line.label}-${index}`}
                                    className={[line.total ? 'is-total' : '', line.discount ? 'is-discount' : '', line.sub ? 'is-sub' : ''].filter(Boolean).join(' ')}
                                >
                                    <span>{line.label}</span>
                                    <strong>{line.value}</strong>
                                </li>
                            ))}
                        </ul>
                        <ul className='confirm-dl-rows confirm-pay-meta'>
                            <DlRow label="Payment status" value={payment.status_label || payment.status || booking.status_label} />
                            <DlRow label="Payment Method" value={paymentMethod} />
                            <DlRow label="Paid amount" value={
                                payment.total_booking_amount_label
                                || booking.total_amount_label
                                || payment.amount_label
                                || payment.amount
                            } />
                            <DlRow label="Payment Date" value={payment.paid_at_label || payment.paid_at} />
                            {coupon.code ? <DlRow label="Coupon" value={coupon.code} /> : null}
                            <DlRow label="Booking ID" value={booking.id ? `#${booking.id}` : ''} />
                            {providerBookingId ? <DlRow label="Booking reference" value={providerBookingId} /> : null}
                        </ul>
                    </article>

                    {refund ? (
                        <>
                            <h3 className='confirm-section-title'>Refund Details</h3>
                            <article className='confirm-card'>
                                <ul className='confirm-dl-rows'>
                                    <DlRow label="Status" value={refund.status_label || refund.status} />
                                    <DlRow label="Refund amount" value={refund.amount_label || (refund.amount != null ? formatInr(refund.amount) : '')} />
                                    <DlRow label="To wallet" value={refund.refund_to_wallet_label || (refund.refund_to_wallet != null ? formatInr(refund.refund_to_wallet) : '')} />
                                    <DlRow label="To gateway" value={refund.refund_to_gateway_label || (refund.refund_to_gateway != null ? formatInr(refund.refund_to_gateway) : '')} />
                                </ul>
                            </article>
                        </>
                    ) : null}

                    <div className='trips-rating'>
                        <TripRatingSection bookingId={bookingId} />
                    </div>

                    <h3 className='confirm-section-title'>Important Information</h3>
                    <article className='confirm-card confirm-info'>
                        <ul>
                            {FLIGHT_IMPORTANT_INFO.map((infoItem, index) => (
                                <li key={index}>{infoItem}</li>
                            ))}
                        </ul>
                    </article>

                    <div className='confirm-footer'>
                        {booking.actions?.can_continue_booking ? (
                            <button type='button' className='confirm-btn-primary' onClick={() => navigate('/payment')}>Continue booking</button>
                        ) : null}
                        {booking.actions?.can_pay ? (
                            <button type='button' className='confirm-btn-primary' onClick={() => navigate('/payment')}>Complete payment</button>
                        ) : (
                            <button type='button' className='confirm-btn-primary' onClick={() => navigate('/home')}>Book another flight</button>
                        )}
                        <div className='confirm-footer-row'>
                            <button type='button' className='confirm-btn-outline' onClick={() => navigate(-1)}>Back to trips</button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )

}

export default TripDoc
